<template>
  <div>
    <my-header></my-header>

    <div class="content-container-level-1">
      <h2>Schedule daily quest</h2>

      <form @submit.prevent="createRecordWithConfirmation">

        <div class="form-group">        
          <label for="quest">Quest (example: @daily_quests/quests/beatle)</label>

          <vue-bootstrap-typeahead
            v-model="questAlias"
            :data="questSuggestions"
            :minMatchingChars="1"
            required></vue-bootstrap-typeahead>
        </div>

        <hr>

        <div class="form-group">
          <label for="start-at">Start at:</label>
          <date-time-input id="start-at" v-model="startAt"></date-time-input>
        </div>

        <hr>

        <div class="form-group">
          <h5>Duration</h5>
          <duration-input v-model="duration"></duration-input>
          <p class="mb-3">
            End at<br>
            {{endAt | getMomentStr}}
          </p>
        </div>

        <hr>

        <div class="form-group">
          <label for="minLevel">Min level</label>
          <input v-model.number="minLevel" id="minLevel" class="form-control" type="number" min="0">
        </div>

        <hr>

        <div class="form-group">
          <h5>Min time until end for start</h5>
          <duration-input v-model="minTimeUntilEndForStart"></duration-input>
          <p class="mb-3">
            Start available until<br>
            {{startAvailableUntil | getMomentStr}}
          </p>
        </div>

        <hr>

        <div class="form-group">
          <h5><label for="minClientVersion">Min client version</label></h5>
          <input v-model="minClientVersion" id="minClientVersion" class="form-control">
          <p class="form-text">
            Min value (forced version): {{forcedVersion}}
          </p>
          <p class="form-text">
            Max value (server version): {{serverVersion}}
          </p>
        </div>

        <hr>

        <div class="form-group">
          <b-form-checkbox id="checkbox-for-qa-users" v-model="forQA">For QA only</b-form-checkbox>
        </div>

        <hr>

        <div class="form-group">
          <b-form-checkbox v-model="isRepeatRequired">Repeat</b-form-checkbox>
        </div>

        <fieldset class="form-group" v-if="isRepeatRequired">
          <legend>Repetition periods</legend>
          <div class="form-group">
            <label for="pause-between-quests">Pause between daily quests (hour):</label>
            <b-form-input id="pause-between-quests" v-model="pauseBetweenQuests" type="number" number required></b-form-input>
          </div>
          <div class="form-group">
            <label for="repeat-until">Repeat until:</label>
            <date-time-input id="repeat-until" v-model="repeatUntil"></date-time-input>
          </div>
          <periods-table :periods="repeatPeriods"></periods-table>
        </fieldset>

        <b-button :to="{name: 'daily_quests_timetable'}">Cancel</b-button>
        <b-button type="submit" variant="primary">Create</b-button>
      </form>
    </div>

    <loading-indicator v-if="isLoading"/>
  </div>
</template>

<script>
import { confirmAction } from '../../service/confirm';

import {
  getMomentStr,
  convertVersionCodeToStr,
  getProtoId,
  Period,
  getRepeatedPeriods,
} from '../../util';

import notifier from '../../notifier';
import { HTTP } from '../../http-common';

import MyHeader from '../Header.vue';
import LoadingIndicator from '../LoadingIndicator.vue';
import DateTimeInput from '../DateTimeInput.vue';
import DurationInput from '../DurationInput.vue';
import VueBootstrapTypeahead from '../VueBootstrapTypeahead.vue';
import PeriodsTable from '../PeriodsTable.vue';

export default {
  name: 'AddDailyQuestsTimetableRecord',

  components: {
    MyHeader,
    LoadingIndicator,
    DateTimeInput,
    DurationInput,
    VueBootstrapTypeahead,
    PeriodsTable,
  },

  data() {
    let now = Date.now();
    now -= (now % 60000);

    return {
      isLoading: false,
      
      questAlias: '',
      questSuggestions: [],

      startAt: now,
      duration: 0,
      minLevel: 0,
      minTimeUntilEndForStart: 0,

      forcedVersion: '',
      serverVersion: '',
      minClientVersion: '',
      forQA: false,

      isRepeatRequired: false,
      pauseBetweenQuests: 24,
      repeatUntil: now,
    };
  },

  computed: {
    endAt() {
      return this.startAt/1000 + this.duration;
    },

    startAvailableUntil() {
      return this.endAt - this.minTimeUntilEndForStart;
    },

    repeatPeriods() {
      const startPeriod = new Period(this.startAt/1000, this.endAt);
      return getRepeatedPeriods(startPeriod, this.pauseBetweenQuests * 3600, this.repeatUntil/1000);
    },
  },

  created() {
    this.loadLastRecord();
  },

  watch: {
    questAlias(newQuestAlias) {
      HTTP.get(
        '/daily_quests/configs',
        {
          params: {
            q: newQuestAlias
          }
        }
      )
      .then(response => {
        const aliases = [];
        for(var i = 0; i < response.data.data.length; ++i) {
          const alias = response.data.data[i];
          aliases.push(alias);
        }
        this.questSuggestions = aliases;
      })
      .catch(e => {
        notifier.showError(e);
      })
    },
  },

  methods: {
    loadLastRecord() {
      notifier.deleteSenderNotifications(this);

      this.isLoading = true;

      const url = '/daily_quests/timetable/adding_info';
      HTTP.get(url)
      .then(response => {
        const data = response.data.data;
        if(data === null) {
          notifier.showError('Invalid server response. data === null.', {sender: this});
          return;
        }

        const lastRecord = data.last_record;
        if (lastRecord.id > 0) {
          const endTime = (lastRecord.stime+lastRecord.duration)*1000;
          this.startAt = endTime;
          this.minLevel = lastRecord.min_level;
          this.duration = lastRecord.duration;
          this.minTimeUntilEndForStart = lastRecord.min_time_until_end_for_start;
          this.repeatUntil = endTime;
        }

        this.forcedVersion = convertVersionCodeToStr(data.forced_version);
        this.serverVersion = convertVersionCodeToStr(data.server_version);
      })
      .catch(e => {
        notifier.showError(e, {sender: this});
      })
      .finally(() => {
        this.isLoading = false;
      });
    },

    createRecordWithConfirmation() {
      notifier.deleteSenderNotifications(this);

      const record = this.makeRecord();

      this.confirmRecordCreation(() => {
        this.createRecord(record);
      });
    },

    makeRecord() {
      return {
        questProtoId: getProtoId(this.questAlias),
        startAt: this.startAt/1000,
        duration: this.duration,
        minLevel: this.minLevel,
        minTimeUntilEndForStart: this.minTimeUntilEndForStart,
        minClientVersion: this.minClientVersion,
        forQA: this.forQA,
        needToRepeat: this.isRepeatRequired,
        repetitionPeriods: this.repeatPeriods,
      };
    },

    confirmRecordCreation(onConfirmed) {
      confirmAction(
        this,
        'Are you sure you want to schedule this quest {env}?',
        'Create',
        onConfirmed,
      );
    },

    createRecord(record) {
      const data = JSON.stringify(record);

      HTTP.post('/daily_quests/timetable/add', data, {headers: {'Content-Type': 'application/json'}})
      .then(response => {
        const result = response.data.result;

        const RESULT_SUCCESS = 1;
        if(result.code === RESULT_SUCCESS) {
          notifier.showSuccessAfterTransition('Daily quest scheduled.');
          this.$router.push({name: 'daily_quests_timetable'});
          return;
        }

        notifier.showError(result.message, {sender: this});
      })
      .catch(e => {
        notifier.showError(e, {sender: this});
      });
    },
  },

  filters: {
    getMomentStr,
    convertVersionCodeToStr,
  },
};
</script>
